import { uniqueSortedStrings } from '@/utils/arrays';
import { MEDIA_STALE_TIME } from '@/utils/media';
import { MediaPackType } from '@/utils/media/types';
import { MediaSimilarGuides } from '@calm/evexia-web-sdk';
import { createBffApi } from '@rhg/libs/api/create-bff-api';
import createMediaV2Api from '@rhg/libs/api/create-media-v2-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import {
    checkQueryCache,
    invalidateQueryKey,
    ItemToQueryMapping,
    queryClient,
    queryKeys,
    useQuery,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { useActiveCircleId } from '../use-active-circle';
import { useBffFlag } from '../use-bff-flag';
import { useCircle } from '../use-circle';
import { cacheProgramMetadata } from '../use-pack-guide-program-metadata/cache';

export const packsQueryOptions = {
    /**
     * The key should be a pack class map for easy invalidation
     * This allows us to invalidate any query with a given pack class by passing in [queryKeys.packsByPackClass, circleId, { somePackClass: 'somePackClass' }]
     *
     * Refs:
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation#query-matching-with-invalidatequeries
     * https://tanstack.com/query/latest/docs/framework/react/guides/query-keys#array-keys-with-variables
     */
    queryKey: ({
        packClasses,
        circleId,
    }: {
        packClasses?: string[];
        circleId: number;
    }) => {
        return [
            queryKeys.packsByPackClass,
            circleId,
            packClasses
                ? packClasses.reduce(
                      (packClassMap, packClass) => ({
                          ...packClassMap,
                          [packClass]: packClass,
                      }),
                      {}
                  )
                : {},
        ];
    },
    queryFn: async ({
        getAccessTokenSilently,
        packClasses,
        circleId,
        circleAuthorizationToken,
        rHGApp,
        bffFlagEnabled = false,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        packClasses: string[];
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
        bffFlagEnabled?: boolean;
    }) => {
        if (bffFlagEnabled) {
            const bffApi = createBffApi(getAccessTokenSilently);
            const response = await bffApi.getPacks({
                circleId,
                circleAuthorizationToken,
                rHGApp,
                packIds: uniqueSortedStrings(packClasses),
            });
            return response.data.packs;
        }

        const mediaApi = createMediaV2Api(getAccessTokenSilently);

        const response = await mediaApi.v2GetPacks({
            circleAuthorizationToken,
            recipientId: circleId,
            packId: uniqueSortedStrings(packClasses),
            rHGApp,
        });

        return response.data.packs as MediaPackType[];
    },
    queryOptions: ({
        packClasses,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
        isEnabled,
    }: {
        packClasses: string[];
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
        isEnabled: boolean;
    }) => {
        return {
            enabled: !!(
                isEnabled &&
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken &&
                packClasses?.length
            ),
            staleTime: MEDIA_STALE_TIME,
        };
    },
};

export function cachePacks(
    packs: MediaPackType[] | undefined,
    circleId: number,
    _queryClient = queryClient
) {
    packs?.forEach((pack) => {
        const key = packsQueryOptions.queryKey({
            packClasses: [pack.packClass],
            circleId,
        });
        _queryClient.setQueryData<MediaPackType[]>(key, [pack]);
    });

    const programs = packs?.flatMap((pack) =>
        pack.items.filter((item) => item.type === 'program')
    );
    cacheProgramMetadata(programs, circleId, _queryClient);
}

export function invalidatePacks({
    packClasses,
    circleId,
}: {
    packClasses: string[];
    circleId: number;
}) {
    if (!packClasses?.length) {
        return;
    }

    packClasses.forEach((packClass) => {
        invalidateQueryKey(
            packsQueryOptions.queryKey({
                packClasses: [packClass],
                circleId: circleId,
            })
        ).catch((err: unknown) =>
            logger.error(
                'invalidatePacks: failed to invalidate pack query',
                {},
                err as Error
            )
        );
    });

    const forYouPackQueryKey = [queryKeys.forYouRecommendations, circleId];
    queryClient
        .invalidateQueries({
            queryKey: forYouPackQueryKey,
            predicate: (query) => {
                if (!query.queryKey) {
                    return false;
                }

                const queryState = query.state;
                const response = queryState.data as
                    | (Omit<MediaSimilarGuides, 'pack'> & {
                          pack: MediaPackType;
                      })
                    | undefined;
                const pack = response?.pack;
                if (!pack) {
                    return false;
                }
                const packMatchesClass = packClasses.includes(pack.packClass);
                return packMatchesClass;
            },
        })
        .catch((err: unknown) =>
            logger.error(
                'invalidatePacks: failed to invalidate forYou query',
                {},
                err as Error
            )
        );
}

export function usePacks(
    packClasses: string[] = [],
    circleId: number,
    isOrdered = false,
    isEnabled = true
) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = packsQueryOptions;
    const rHGApp = getRhgAppHeader();
    const { bffFlagEnabled } = useBffFlag();

    const options = queryOptions({
        packClasses,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
        isEnabled,
    });

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({ packClasses, circleId }),
        queryFn: async () => {
            if (!packClasses.length) return [];

            const keyObjects = packClasses.map(
                (packClass): ItemToQueryMapping<string> => ({
                    item: packClass,
                    queryKey: queryKey({ packClasses: [packClass], circleId }),
                })
            );

            const { cachedQueries, invalidQueries } = checkQueryCache<
                string,
                MediaPackType[]
            >(keyObjects, options.staleTime);
            const cachedQueriesData = cachedQueries.reduce<MediaPackType[]>(
                (cachedData, item) => [...cachedData, ...item.data],
                []
            );

            // If all packs are already fetched and not stale, return them
            if (invalidQueries.length === 0) {
                return cachedQueriesData;
            }

            const packClassesToFetch = invalidQueries.map(({ item }) => item);

            // Fetch the packs we need
            const response = await queryFn({
                getAccessTokenSilently,
                packClasses: packClassesToFetch,
                circleId,
                circleAuthorizationToken,
                rHGApp,
                bffFlagEnabled,
            });

            // populate cache for each pack class
            cachePacks(response, circleId);

            // Combine newly fetched packs with existing packs
            return [...cachedQueriesData, ...(response || [])];
        },
        ...options,
    });

    // Order packs
    const packs = isOrdered
        ? packClasses.reduce<MediaPackType[]>((orderedPacks, packClass) => {
              const pack = data?.find((pack) => pack.packClass === packClass);
              if (pack) {
                  return [...orderedPacks, pack];
              } else {
                  return orderedPacks;
              }
          }, [])
        : data;

    return {
        packs,
        packMap: packs?.reduce<Record<string, MediaPackType>>(
            (packClassMap, pack) => ({
                ...packClassMap,
                [pack.packClass]: pack,
            }),
            {}
        ),
        isEnabled: options.enabled,
        ...otherProps,
    };
}

export function useActiveCirclePacks(
    packClasses: string[] = [],
    isOrdered = false
) {
    const { activeCircleId } = useActiveCircleId();
    return usePacks(packClasses, activeCircleId, isOrdered);
}
